/**
 * Grida Editor Test Page
 * 
 * This is a test/mock implementation of what Grida editor would do:
 * 1. Consume handoff token from URL
 * 2. Load page config draft
 * 3. Display simple editor UI
 * 4. Save draft back to Frappe
 * 5. Open preview in iframe
 * 
 * This page helps verify the integration APIs work correctly.
 */

import { useEffect, useState, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useGridaTokenConsume } from "./hooks/useGridaTokenConsume";
import { useGridaPageConfigDraft, useSaveGridaPageConfigDraft } from "./hooks/useGridaPageConfigDraft";
import { Button } from "@/components/ui/button";
import { routes } from "@/lib/config/routes";
import { STOREFRONT_ORIGIN } from "@/lib/config/preview-origins";
import { logger } from "@/lib/utils/logger";

export function GridaEditorTestPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");
  const pageConfigNameFromUrl = searchParams.get("pageConfig");

  // Step 1: Consume token if provided (optional - can skip if pageConfig is in URL)
  const { data: tokenData, isLoading: isLoadingToken, error: tokenError } = useGridaTokenConsume(token || null);

  // Determine page config name (from token or URL)
  const pageConfigName = tokenData?.page_config_name || pageConfigNameFromUrl || "";

  // Step 2: Load page config draft
  const { data: draftData, isLoading: isLoadingDraft, error: draftError } = useGridaPageConfigDraft(pageConfigName);

  // Step 3: Create preview session and get preview URL
  const [previewSessionId, setPreviewSessionId] = useState<string | null>(null);
  const [isCreatingSession, setIsCreatingSession] = useState(false);
  
  // Create preview session when draft data is loaded
  useEffect(() => {
    // Backend returns parsed sections in data.sections (not sections_json)
    if (!draftData?.data?.sections || isCreatingSession || previewSessionId) return;
    
    const createPreviewSession = async () => {
      setIsCreatingSession(true);
      try {
        // Backend already parses sections_json into sections array
        const sections = draftData.data.sections;
        
        // Handle both v1 array and v2 object formats
        const sectionsArray = Array.isArray(sections) 
          ? sections 
          : (sections?.sections || []);
        
        if (!Array.isArray(sectionsArray) || sectionsArray.length === 0) {
          logger.warn('[GridaEditorTestPage] No sections found in page config');
          setIsCreatingSession(false);
          return;
        }
        
        // Create preview session via storefront API
        const response = await fetch(`${STOREFRONT_ORIGIN}/api/preview/session`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            sections: sectionsArray,
            tenantId: draftData.data.tenant || undefined,
          }),
        });
        
        if (response.ok) {
          const data = await response.json();
          if (data.sessionId) {
            setPreviewSessionId(data.sessionId);
            logger.log('[GridaEditorTestPage] Preview session created:', data.sessionId);
          } else {
            logger.error('[GridaEditorTestPage] No sessionId in response:', data);
          }
        } else {
          const errorText = await response.text();
          logger.error('[GridaEditorTestPage] Failed to create preview session:', response.status, errorText);
        }
      } catch (error) {
        logger.error('[GridaEditorTestPage] Error creating preview session:', error);
      } finally {
        setIsCreatingSession(false);
      }
    };
    
    createPreviewSession();
  }, [draftData?.data?.sections, isCreatingSession, previewSessionId]);

  // Build preview URL with sessionId
  const previewUrl = previewSessionId 
    ? `${STOREFRONT_ORIGIN}/preview/page?sessionId=${previewSessionId}${draftData?.data?.tenant ? `&tenant=${draftData.data.tenant}` : ''}`
    : `${STOREFRONT_ORIGIN}/preview/page`;

  // Step 4: Save mutation
  const saveMutation = useSaveGridaPageConfigDraft(pageConfigName);

  // Editor state (simple JSON editor for testing)
  const [sectionsJson, setSectionsJson] = useState<string>("");
  const [hasChanges, setHasChanges] = useState(false);
  const previewIframeRef = useRef<HTMLIFrameElement>(null);

  // Initialize editor with draft data
  useEffect(() => {
    if (draftData?.data?.sections_json) {
      try {
        const parsed = typeof draftData.data.sections_json === 'string'
          ? JSON.parse(draftData.data.sections_json)
          : draftData.data.sections_json;
        const currentJson = JSON.stringify(parsed, null, 2);
        setSectionsJson(currentJson);
        setHasChanges(false);
      } catch (e) {
        logger.error('[GridaEditorTestPage] Failed to parse sections_json for editor:', e);
        setSectionsJson(draftData.data.sections_json);
      }
    }
  }, [draftData]);

  // Handle save
  const handleSave = async () => {
    try {
      // Validate JSON
      const parsed = JSON.parse(sectionsJson);
      
      await saveMutation.mutateAsync({
        sections_json: JSON.stringify(parsed),
      });

      setHasChanges(false);
      alert("Saved successfully!");
    } catch (error) {
      logger.error("[GridaEditorTestPage] Save failed:", error);
      alert(`Save failed: ${error instanceof Error ? error.message : "Unknown error"}`);
    }
  };

  // Handle JSON change
  const handleJsonChange = (value: string) => {
    setSectionsJson(value);
    try {
      JSON.parse(value); // Validate JSON
      setHasChanges(true);
    } catch {
      // Invalid JSON, but allow editing
      setHasChanges(true);
    }
  };

  // Handle preview message (for testing postMessage)
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // Only accept messages from preview origin
      if (event.origin !== STOREFRONT_ORIGIN) return;

      logger.log("[GridaEditorTestPage] Received message from preview:", event.data);

      // Handle preview-ready message
      if (event.data?.type === "preview-ready") {
        logger.log("[GridaEditorTestPage] Preview is ready");
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, []);

  // Send test message to preview
  const sendTestMessage = () => {
    if (!previewIframeRef.current?.contentWindow) {
      alert("Preview iframe not loaded");
      return;
    }

    try {
      const sections = JSON.parse(sectionsJson);
      previewIframeRef.current.contentWindow.postMessage(
        {
          type: "section-updated",
          protocolVersion: 1,
          sectionId: "sections[0]",
          sections: sections,
          source: "builder",
        },
        STOREFRONT_ORIGIN
      );
      logger.log("[GridaEditorTestPage] Sent test message to preview");
    } catch (error) {
      alert(`Invalid JSON: ${error instanceof Error ? error.message : "Unknown error"}`);
    }
  };

  // Loading states
  if (token && isLoadingToken) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-muted-foreground">Consuming handoff token...</div>
      </div>
    );
  }

  // Show token error but allow continuing if pageConfig is in URL
  if (token && tokenError && !pageConfigNameFromUrl) {
    return (
      <div className="flex items-center justify-center h-screen flex-col gap-4">
        <div className="text-destructive text-lg font-semibold">Token Error</div>
        <div className="text-muted-foreground">{tokenError.message}</div>
        {tokenData?.error && (
          <div className="text-sm text-destructive">
            Backend error: {tokenData.error}
          </div>
        )}
        <div className="text-sm text-muted-foreground">
          Tip: You can also access this page with: <code>/grida-editor-test?pageConfig=YOUR_PAGE_CONFIG_NAME</code>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => navigate(routes.storefrontPageConfig.list)}>
            Go Back
          </Button>
          <Button variant="outline" onClick={() => {
            const pageConfig = prompt("Enter page config name manually:");
            if (pageConfig) {
              navigate(`/grida-editor-test?pageConfig=${pageConfig}`);
            }
          }}>
            Enter Page Config Manually
          </Button>
        </div>
      </div>
    );
  }

  if (!pageConfigName) {
    return (
      <div className="flex items-center justify-center h-screen flex-col gap-4">
        <div className="text-destructive text-lg font-semibold">Missing Page Config</div>
        <div className="text-muted-foreground">
          {token ? (
            <>
              Token consumed but no page config found.
              {tokenError && (
                <div className="mt-2 text-sm">
                  Error: {tokenError.message}
                </div>
              )}
              {tokenData?.error && (
                <div className="mt-2 text-sm">
                  Backend error: {tokenData.error}
                </div>
              )}
              <div className="mt-2 text-sm">
                Token may be expired (60s) or already used (one-time use).
              </div>
            </>
          ) : (
            "No page config specified"
          )}
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => navigate(routes.storefrontPageConfig.list)}>
            Go Back
          </Button>
          {token && (
            <Button variant="outline" onClick={() => {
              // Try to extract page config from URL or suggest manual entry
              const urlParams = new URLSearchParams(window.location.search);
              const pageConfig = prompt("Enter page config name manually:", urlParams.get("pageConfig") || "");
              if (pageConfig) {
                navigate(`/grida-editor-test?pageConfig=${pageConfig}`);
              }
            }}>
              Enter Page Config Manually
            </Button>
          )}
        </div>
      </div>
    );
  }

  if (isLoadingDraft) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-muted-foreground">Loading page config draft...</div>
      </div>
    );
  }

  if (draftError) {
    return (
      <div className="flex items-center justify-center h-screen flex-col gap-4">
        <div className="text-destructive text-lg font-semibold">Load Error</div>
        <div className="text-muted-foreground">{draftError.message}</div>
        <Button variant="outline" onClick={() => navigate(routes.storefrontPageConfig.detail(pageConfigName))}>
          Go Back
        </Button>
      </div>
    );
  }

  // previewUrl is defined above in the session creation useEffect

  return (
    <div className="flex h-screen flex-col">
      {/* Header */}
      <div className="border-b bg-background p-4 flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold">Grida Editor Test</h1>
          <p className="text-sm text-muted-foreground">
            Page Config: {pageConfigName}
            {tokenData?.user && ` • User: ${tokenData.user.email}`}
            {token && ` • Token: ${token.substring(0, 20)}...`}
          </p>
          {!token && (
            <p className="text-xs text-muted-foreground mt-1">
              No token provided. Using pageConfig from URL. To test token flow, click "Open Grida Editor" button on detail page.
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => navigate(routes.storefrontPageConfig.detail(pageConfigName))}>
            Back to Detail
          </Button>
          <Button
            onClick={handleSave}
            disabled={!hasChanges || saveMutation.isPending || !sectionsJson}
          >
            {saveMutation.isPending ? "Saving..." : "Save Draft"}
          </Button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex flex-1 overflow-hidden">
        {/* Left: Editor */}
        <div className="w-1/2 border-r flex flex-col">
          <div className="p-4 border-b">
            <h2 className="font-semibold mb-2">Sections JSON Editor</h2>
            <p className="text-sm text-muted-foreground">
              Edit the sections JSON. Changes will be saved to Frappe.
            </p>
          </div>
          <div className="flex-1 overflow-auto p-4">
            <textarea
              value={sectionsJson}
              onChange={(e) => handleJsonChange(e.target.value)}
              className="w-full h-full font-mono text-sm p-2 border rounded-md"
              placeholder='[{"type": "text-block", "slots": {...}}]'
            />
          </div>
          <div className="p-4 border-t">
            <Button variant="outline" onClick={sendTestMessage} className="w-full">
              Send Test Message to Preview
            </Button>
          </div>
        </div>

        {/* Right: Preview */}
        <div className="w-1/2 flex flex-col">
          <div className="p-4 border-b">
            <h2 className="font-semibold mb-2">Preview</h2>
            <p className="text-sm text-muted-foreground">
              {isCreatingSession ? (
                "Creating preview session..."
              ) : previewSessionId ? (
                `Preview URL: ${previewUrl}`
              ) : (
                "Waiting for page config to load..."
              )}
            </p>
          </div>
          <div className="flex-1 overflow-hidden">
            {previewSessionId ? (
              <iframe
                ref={previewIframeRef}
                src={previewUrl}
                className="w-full h-full border-0"
                title="Preview"
              />
            ) : (
              <div className="flex items-center justify-center h-full text-muted-foreground">
                {isCreatingSession ? "Creating session..." : "Loading page config..."}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
